import { Divider, Icon, Item, Label } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import offerTypes from "astrid-firestore/src/api/offers/constants/offerTypes";
import getAuthorNames from "astrid-firestore/src/api/productions/team/getAuthorNames";
import { languageName } from "astrid-helpers/src/languages";

import Flex from "../../../../../components/Flex/Flex";
import { formatPrice } from "../../../../prices/utils/priceFormatters";
import getReaderNames from "../../../../productions/utils/getReaderNames";

import useOfferPrice from "../../../hooks/useOfferPrice";

export function OfferDetailLabel({ label, detail, icon }) {
	if (!detail) return null;

	return (
		<Label basic size="tiny" style={{ margin: 2 }}>
			{label} <Label.Detail>{detail}</Label.Detail>
			{icon}
		</Label>
	);
}

export default function OfferInformation({ production, offer, work, hidePrice, children }) {
	const { t } = useTranslation();

	const price = useOfferPrice(offer);

	const [fullSynopsis, setFullSynopsis] = useState(false);

	const synopsis = work?.synopsis || production.synopsis;

	const extendText = fullSynopsis || !(synopsis?.length > 800);

	const series = work?.serie?.name || production.series;

	const part = work?.serieNumber || production.part;

	const authors = (work && getAuthorNames(work)) || getAuthorNames(production);

	const otherLanguages = work?.additionalLanguages?.length ? work.additionalLanguages : production.languageExtra;

	return (
		<>
			<Item.Group divided>
				<Item>
					<Item.Image size="small" src={production.img || work?.coverThumb?.url || "/img/image.png"} />

					<Item.Content>
						<Item.Header>{production.title}</Item.Header>

						<Item.Meta>{production.publisherName}</Item.Meta>

						{synopsis && (
							<Item.Description>
								<p>
									{extendText ? synopsis : synopsis.substring(0, 800) + "... "}

									{synopsis?.length > 800 && (
										<span
											onClick={() => setFullSynopsis(!fullSynopsis)}
											style={{ fontWeight: "bold", cursor: "pointer" }}
										>
											{!fullSynopsis
												? `  ${t("showMore", "Show more")}`
												: `  ${t("showLess", "Show less")}`}
										</span>
									)}
								</p>
							</Item.Description>
						)}

						<Item.Extra>
							<div style={{ marginLeft: "-2px" }}>
								<OfferDetailLabel label={t("isbn")} detail={production.isbn} />

								<OfferDetailLabel
									label={t("deliveryDate")}
									detail={production.deliveryDate?.toDate().toLocaleDateString()}
								/>

								<OfferDetailLabel
									label={t("seriesPart", "Series (part)")}
									detail={series || part ? `${series || "-"} ${part || ""}` : undefined}
								/>

								<OfferDetailLabel label={t("author")} detail={authors} />

								<OfferDetailLabel label={t("pages")} detail={production.pages} />

								<OfferDetailLabel label={t("narrator")} detail={getReaderNames(production)} />

								<OfferDetailLabel label={t("language")} detail={languageName(production.language)} />

								<OfferDetailLabel
									label={t("otherLanguages")}
									detail={otherLanguages
										?.map((langExtra) => {
											return languageName(langExtra);
										})
										.join(", ")}
								/>

								<OfferDetailLabel
									label={t("scriptDate")}
									detail={
										production.scriptUploadDate?.toDate().toLocaleDateString() ||
										production.scriptDate?.toDate().toLocaleDateString()
									}
									icon={
										production.scriptUploadDate && (
											<Icon style={{ marginLeft: "5px" }} name="check" color="green" />
										)
									}
								/>
							</div>
						</Item.Extra>

						<Divider />

						<Flex style={{ marginTop: 5 }}>
							{production.productionDate && (
								<div>
									<strong>{t("productionDate")}: </strong>

									{production.productionDate.toDate().toLocaleDateString()}
								</div>
							)}

							{offer.price && !hidePrice && (
								<div>
									<strong>{t("price")}: </strong>

									{formatPrice(price)}

									{offer?.price?.name === "Custom" && ` (${t("customPrice", "Custom")})`}
								</div>
							)}
						</Flex>

						<Divider />

						{offer.type === offerTypes.WORK_ORDER && (
							<>
								<div>
									<strong>{t("sentBy", "Sent by")}: </strong>

									{offer?.createdBy?.name}
								</div>
								<Divider />
							</>
						)}

						{children}
					</Item.Content>
				</Item>
			</Item.Group>
		</>
	);
}
